import { useMemo } from 'react';
import { Question, BranchingRule, ResponseValue } from '../types';
import { UseBranchingReturn } from '../types/hooks';

/**
 * Hook for managing quiz branching logic
 * Calculates visible questions based on responses and branching rules
 * Detects circular dependencies and validates branching paths
 *
 * @param questions - Array of all quiz questions
 * @param branchingRules - Array of branching rules
 * @param responses - Current responses object (questionId -> answerId)
 * @returns Branching state and actions
 */
export const useBranching = (
  questions: Question[],
  branchingRules: BranchingRule[],
  responses: Record<number, ResponseValue>,
): UseBranchingReturn => {
  /**
   * Compute visible questions and validation state
   * Memoized for performance optimization
   */
  const computedState = useMemo(() => {
    const state = {
      visibleQuestionIds: new Set<number>(),
      hiddenQuestionIds: new Set<number>(),
      errors: [] as string[],
      isValid: true,
    };

    if (questions.length === 0) {
      return {
        visibleQuestionIds: Array.from(state.visibleQuestionIds),
        isValid: true,
        errors: [],
      };
    }

    // Start with all questions visible
    questions.forEach((q) => state.visibleQuestionIds.add(q.id));

    // Track visited questions for circular dependency detection
    const visitedChain = new Set<number>();

    /**
     * Recursively apply branching rules
     */
    const applyBranchingRules = (questionId: number, depth = 0): void => {
      // Prevent infinite recursion and detect cycles
      if (depth > 50) {
        state.errors.push('Maximum recursion depth exceeded in branching rules');
        state.isValid = false;
        return;
      }

      if (visitedChain.has(questionId)) {
        state.errors.push(
          `Circular branching detected involving question ${questionId}`,
        );
        state.isValid = false;
        return;
      }

      visitedChain.add(questionId);

      // Find applicable branching rules for this question
      const applicableRules = branchingRules.filter(
        (rule) => String(rule.sourceQuestionId) === String(questionId),
      );

      applicableRules.forEach((rule) => {
        // Check if the response matches this rule's condition
        const response = Object.entries(responses).find(([id]) => String(id) === String(rule.sourceQuestionId))?.[1];
        const responseValues = Array.isArray(response) ? response : [response];
        const selectedAnswerIds = responseValues
          .map((value) => {
            if (value && typeof value === 'object' && !Array.isArray(value) && 'answerId' in value) {
              return String((value as { answerId: unknown }).answerId);
            }
            return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
          })
          .filter((value): value is string => value !== null);

        let ruleMatches = false;

        ruleMatches = selectedAnswerIds.includes(String(rule.sourceAnswerId));

        if (ruleMatches) {
          // Validate target question exists
          if (!questions.some((q) => String(q.id) === String(rule.targetQuestionId))) {
            state.errors.push(
              `Branching rule references non-existent target question ${rule.targetQuestionId}`,
            );
            state.isValid = false;
            return;
          }

          const action = String(rule.action).toLowerCase();
          const targetId = questions.find((q) => String(q.id) === String(rule.targetQuestionId))!.id;
          if (action === 'skip' || action === 'show' || action === 'goto') {
            state.visibleQuestionIds.add(targetId);
            state.hiddenQuestionIds.delete(targetId);
          }

          if (action === 'skip') {
            // Skip means "jump to the target": questions between source and target are hidden.
            const sourceIndex = questions.findIndex((q) => String(q.id) === String(rule.sourceQuestionId));
            const targetIndex = questions.findIndex((q) => q.id === targetId);
            if (sourceIndex !== -1 && targetIndex !== -1 && sourceIndex !== targetIndex) {
              const start = Math.min(sourceIndex, targetIndex) + 1;
              const end = Math.max(sourceIndex, targetIndex) - 1;
              for (let i = start; i <= end; i += 1) {
                state.visibleQuestionIds.delete(questions[i].id);
                state.hiddenQuestionIds.add(questions[i].id);
              }
            }
          }

          if (action === 'skip' || action === 'show' || action === 'goto') {
            applyBranchingRules(rule.targetQuestionId, depth + 1);
          }
        }
      });

      visitedChain.delete(questionId);
    };

    // Apply branching rules only from currently reachable answered questions
    questions.forEach((question) => {
      if (!state.visibleQuestionIds.has(question.id)) {
        return;
      }

      const response = Object.entries(responses).find(([id]) => String(id) === String(question.id))?.[1];
      if (response !== undefined) {
        applyBranchingRules(question.id);
      }
    });

    return {
      visibleQuestionIds: Array.from(state.visibleQuestionIds),
      isValid: state.isValid,
      errors: state.errors,
    };
  }, [questions, branchingRules, responses]);

  /**
   * Check if a specific question should be visible
   */
  const isQuestionVisible = (questionId: number): boolean => {
    return computedState.visibleQuestionIds.includes(questionId);
  };

  /**
   * Get array of visible questions from the full questions list
   */
  const getVisibleQuestions = (allQuestions: Question[]): Question[] => {
    return allQuestions.filter((q) => isQuestionVisible(q.id));
  };

  /**
   * Validate the current branching path
   * Returns true if all visible questions have responses or are optional
   */
  const validateBranchingPath = (): boolean => {
    if (!computedState.isValid) {
      return false;
    }

    // All visible questions should have responses
    const allVisible = computedState.visibleQuestionIds;
    const allAnswered = allVisible.every((qId) => responses[qId] !== undefined);

    return allAnswered;
  };

  return {
    visibleQuestionIds: computedState.visibleQuestionIds,
    isValid: computedState.isValid,
    errors: computedState.errors,
    isQuestionVisible,
    getVisibleQuestions,
    validateBranchingPath,
  };
};
